import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useDeleteContactFormMutation, useLazyGetContactFormsQuery} from '../../../../../services/cellcast.v2'
import {OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import NodataYet from '../../../../Components/NodataYet'
import ReactPaginate from 'react-paginate'
import {MenuComponent} from '../../../../../_metronic/assets/ts/components'
import WriteModal from './Modal/WriteModal'
import {ContactForm} from '../../../../../services/cellcast.v2.types'
import QrCodeModal from './Modal/QRCode'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import { toast } from 'react-toastify'
import HtmlForm from './Modal/HtmlForm'
import { ENV } from '../../../../Utils/_helpers/EnvHelper'

const ListForm: FC = () => {
  // load contact forms
  const [
    fetchContactForms,
    {data: contactForms, isLoading: contactFormsLoading, isFetching: contactFormFetching},
  ] = useLazyGetContactFormsQuery()
  const [deleteContactForm, {isLoading: deleteLoading}] = useDeleteContactFormMutation()

  const [params, setParams] = useState<any>({
    page: 1,
    limit: 10,
  })
  const [reload, setReload] = useState(false)
  const [isShowPaginate, setShowPaginate] = useState(true)
  const [loadedData, setLoadedData] = useState<{
    startLoad: number
    endLoad: number
  }>({
    startLoad: 0,
    endLoad: 0,
  })
  const [showWriteModal, setShowWriteModal] = useState(false)
  const [showQrCodeModal, setShowQrCodeModal] = useState(false)
  const [selectedForm, setSelectedForm] = useState<ContactForm | undefined>(undefined)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showHtmlModal, setShowHtmlModal] = useState(false)

  const getLoadedData = useCallback(
    (
      page: string | number | undefined,
      limit: string | number | undefined,
      totalData: number
    ): {
      startLoad: number
      endLoad: number
    } => {
      let loadedPage = 1
      if (typeof page === 'string') {
        loadedPage = parseInt(page) || 1
      }

      if (typeof page === 'number') {
        loadedPage = page || 1
      }

      let loadedLimit = 10
      if (typeof limit === 'string') {
        loadedLimit = parseInt(limit) || 1
      }

      if (typeof limit === 'number') {
        loadedLimit = limit || 10
      }

      const startItem = (loadedPage - 1) * loadedLimit + 1
      const endItem = Math.min(loadedPage * loadedLimit, totalData)

      return {
        startLoad: startItem,
        endLoad: endItem,
      }
    },
    []
  )

  useEffect(() => {
    fetchContactForms(params)
  }, [])

  useEffect(() => {
    if (reload) {
      fetchContactForms(params)
    }
    setReload(false)
  }, [reload])

  useEffect(() => {
    if (contactForms) {
      const loadedData = getLoadedData(
        contactForms.data?.current ? contactForms.data?.current : 1,
        params.limit,
        contactForms.data?.total ? contactForms.data?.total : 0
      )
      setLoadedData(loadedData)

      if (contactForms.data.total === 0) {
        setShowPaginate(false)
      } else {
        setShowPaginate(true)
      }
    }
    MenuComponent.reinitialization()
  }, [contactForms, contactFormsLoading])

  // Modal
  const Write = useMemo(
    () => (
      <WriteModal
        contactForm={selectedForm}
        show={showWriteModal}
        handleClose={() => {
          setShowWriteModal(false)
          setSelectedForm(undefined)
        }}
        callbackFn={() => {
          setParams({...params, page: 1, limit: 10, search: ''})
          setShowWriteModal(false)
          setReload(true)
          setSelectedForm(undefined)
        }}
      />
    ),
    [params, showWriteModal, selectedForm]
  )

  const QrCode = useMemo(
    () => (
      <QrCodeModal
        show={showQrCodeModal}
        handleClose={() => {
          setShowQrCodeModal(false)
          setSelectedForm(undefined)
        }}
        contactForm={selectedForm}
      />
    ),
    [showQrCodeModal, selectedForm]
  )

  const HtmlCodeModal = useMemo(
    () => (
      <HtmlForm
        show={showHtmlModal}
        handleClose={() => {
          setShowHtmlModal(false)
          setSelectedForm(undefined)
        }}
        contactForm={selectedForm}
      />
    ),
    [showHtmlModal, selectedForm]
  )

  const Delete = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={showDeleteConfirmation}
        handleClose={() => {
          setShowDeleteConfirmation(false)
            setSelectedForm(undefined)
        }}
        Message={`Are you sure, you want to delete ${selectedForm?.name} Form ?`}
        confirm={() => {
          deleteContactForm({
            id: selectedForm?._id || '',
          })
            .then((res) => {
              if ('data' in res) {
                toast.success(res.data?.message)
              } else if ('error' in res) {
                toast.error(res.error)
                console.error('Error delete contact:', res.error)
              }
              setSelectedForm(undefined)
              setShowDeleteConfirmation(false)
            }).finally(() => {
              setReload(true)
            })
        }}
        subTitle={'These form will be permanently deleted from your account.'}
      />
    ),
    [selectedForm, showDeleteConfirmation]
  )

  return (
    <div className='card mb-0'>
      {Write}
      {QrCode}
      {Delete}
      {HtmlCodeModal}
      <div className='card-header border-0 pt-12'>
        <h3 className='card-title align-items-start flex-column'>
          <div>
            <span className='fw-bolder fs-2 mb-1'>Lead Capture Form</span>
            <OverlayTrigger
                placement='bottom'
                overlay={<Tooltip id="tooltip-bottom">
                  <div>
                    <span>Collect customer information directly from your website, social media, or digital ads and automatically store it in your Cellcast account for marketing or sales follow-up.</span>
                  </div>
                </Tooltip>}
              >
                <i
                  className='m-2 svg-icon-3 svg-icon-gray-500 cursor-pointer bi-info-circle'
                />
            </OverlayTrigger>
          </div>
          <span className='text-muted mt-1 fw-bold fs-7'>See all lead capture forms</span>
        </h3>
        
        <div className='card-toolbar m-0'>
          <div className='d-flex gap-3 align-items-center'>
            <button
              className='btn btn-primary'
              onClick={() => {
                setShowWriteModal(true)
              }}
            >
              Create Form
            </button>
          </div>
        </div>
      </div>
      {contactFormsLoading ? (
        <div className='row flex-center h-200 mb-10'>
          <Spinner />
        </div>
      ) : (
        <>
          {contactForms?.data?.items && contactForms?.data?.items.length > 0 ? (
            <div className='card-body'>
              <div className='table-responsive'>
                <table className='table align-middle gs-0 gy-5 all-message-table'>
                  <thead>
                    <tr className='fw-400 text-muted'>
                      <th>Form Name</th>
                      <th>Description</th>
                      <th>Tags</th>
                      <th>Status</th>
                      <th className='w-100px'>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactFormsLoading ? (
                      <tr>
                        <td colSpan={5} className='text-center mb-10'>
                          <Spinner />
                        </td>
                      </tr>
                    ) : (
                      <>
                        {contactForms?.data?.items?.map((item, i: number) => (
                          <tr key={i}>
                            <td onClick={() => {}} className='fw-bold cursor-pointer px-2'>
                              {item?.name}
                            </td>
                            <td>{item.description}</td>
                            <td>
                              {item.tags?.map((tag) => {
                                if (tag.name?.length > 0) {
                                  return (
                                    <div
                                      key={tag.name}
                                      className='inbox-tags'
                                      style={{
                                        width: 'fit-content',
                                      }}
                                    >
                                      {tag.name}
                                    </div>
                                  )
                                }
                              })}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  item.isActive
                                    ? 'green_text_badge badge-light-success'
                                    : 'red_text_badge badge-light-danger'
                                } badge-square p-4 py-5`}
                              >
                                {item.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div className='row'>
                                <span
                                  className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary'
                                  data-kt-menu-trigger='hover'
                                  data-kt-menu-placement='bottom-end'
                                >
                                  {(contactFormsLoading || contactFormFetching) &&
                                  selectedForm &&
                                  selectedForm._id === item._id ? (
                                    <Spinner animation='grow' size='sm' />
                                  ) : (
                                    <i className='bi bi-three-dots fs-5'></i>
                                  )}
                                </span>

                                <div
                                  className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-state-bg-light-primary fw-bold fs-7 w-200px py-4'
                                  data-kt-menu={
                                    contactFormsLoading || contactFormFetching ? 'false' : 'true'
                                  }
                                >
                                  <div className='menu-item px-3'>
                                    <span
                                      className='menu-link px-3'
                                      onClick={() => {
                                        setSelectedForm(item)
                                        setShowQrCodeModal(true)
                                      }}
                                    >
                                      <i className='bi bi-qr-code-scan me-2'></i>Show QR Code
                                    </span>
                                  </div>
                                  <div className='menu-item px-3'>
                                    <span
                                      className='menu-link px-3'
                                      onClick={() => {
                                        navigator.clipboard.writeText(`${ENV().REACT_APP_FE_URL}/contact-form/${item._id}`)
                                        toast.success('Successfully copied lead form link')
                                      }}
                                    >
                                      <i className='bi bi-clipboard me-2'></i>Copy Link
                                    </span>
                                  </div>
                                  <div className='menu-item px-3'>
                                    <span
                                      className='menu-link px-3'
                                      onClick={() => {
                                        setSelectedForm(item)
                                        setShowWriteModal(true)
                                      }}
                                    >
                                      <i className='bi bi-pencil me-2'></i>Edit
                                    </span>
                                  </div>
                                  <div className='menu-item px-3'>
                                    <span
                                      className='menu-link px-3'
                                      onClick={() => {
                                        setSelectedForm(item)
                                        setShowDeleteConfirmation(true)
                                      }}
                                    >
                                      <i className='bi bi-trash me-2'></i>Delete
                                    </span>
                                  </div>
                                  <div className='menu-item px-3'>
                                    <span
                                      className='menu-link px-3'
                                      onClick={() => {
                                        setSelectedForm(item)
                                        setShowHtmlModal(true)
                                      }}
                                    >
                                      <i className='bi  bi-code me-2'></i>Show HTML
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              {contactFormsLoading ? (
                <div className='row flex-center h-200'>
                  <Spinner />
                </div>
              ) : (
                <NodataYet message={`You don't have any list yet`} />
              )}
            </>
          )}
        </>
      )}
      {isShowPaginate && (
        <div className='abcd ps-10 pe-5 pb-5'>
          <select
            onChange={(e: any) => {
              setParams({...params, limit: e.target.value})
              setReload(true)
            }}
            value={params.limit || 10}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          {contactFormsLoading
            ? '. . . . .'
            : `Show ${loadedData.startLoad} - ${loadedData.endLoad} of ${
                contactForms?.data?.total ? contactForms?.data?.total : 0
              } data`}

          <ReactPaginate
            previousLabel='<'
            nextLabel='>'
            pageCount={contactForms?.data?.totalPages || 0}
            onPageChange={(e: any) => {
              setParams({...params, page: e.selected + 1})
              setReload(true)
            }}
            containerClassName={'pagination'}
            previousLinkClassName={'pagination__link'}
            nextLinkClassName={'pagination__link'}
            disabledClassName={'pagination__link--disabled'}
            activeClassName={'pagination__link--active'}
          />
        </div>
      )}
    </div>
  )
}

export default ListForm
